import type { MarketingAnalyticsSnapshot } from "../../shared/types";
import { ApiRequestError, getRequired } from "./apiClient";

const marketingAnalyticsCacheTtlMs = 10 * 60 * 1000;
let cachedMarketingAnalytics: { expiresAt: number; snapshot: MarketingAnalyticsSnapshot } | undefined;
let inFlightMarketingAnalytics: Promise<MarketingAnalyticsSnapshot> | undefined;

export function readCachedMarketingAnalytics(): MarketingAnalyticsSnapshot | undefined {
  if (!cachedMarketingAnalytics || cachedMarketingAnalytics.expiresAt <= Date.now()) {
    return undefined;
  }

  return cachedMarketingAnalytics.snapshot;
}

export function getMarketingAnalytics(options: { forceRefresh?: boolean } = {}): Promise<MarketingAnalyticsSnapshot> {
  const cached = readCachedMarketingAnalytics();
  if (!options.forceRefresh && cached) {
    return Promise.resolve(cached);
  }

  if (inFlightMarketingAnalytics) {
    return inFlightMarketingAnalytics;
  }

  inFlightMarketingAnalytics = getRequired<MarketingAnalyticsSnapshot>("/marketing/analytics")
    .then((snapshot) => {
      cachedMarketingAnalytics = {
        expiresAt: Date.now() + marketingAnalyticsCacheTtlMs,
        snapshot
      };
      return snapshot;
    })
    .catch((error) => {
      if (!(error instanceof TypeError) && !(error instanceof ApiRequestError && error.status >= 500)) throw error;
      const previous = cachedMarketingAnalytics?.snapshot;
      if (!previous?.delivery) throw error;
      return { ...previous, delivery: { ...previous.delivery, status: "stale" as const } };
    })
    .finally(() => {
      inFlightMarketingAnalytics = undefined;
    });

  return inFlightMarketingAnalytics;
}
